import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Layers, FileText, UserCheck, CheckCircle, XCircle, RotateCcw, Clock } from 'lucide-react'

interface ReviewRequest {
  request_id: string
  resource_type: 'pre_read' | 'report' | 'validation_job'
  resource_id: string
  project_id: string
  requested_by_user_id: string
  message?: string
  status: string
  created_at: number
}

const RESOURCE_ICONS = {
  pre_read: Layers,
  report: FileText,
  validation_job: UserCheck,
}

const RESOURCE_LABELS = {
  pre_read: 'Pre-read',
  report: 'Report',
  validation_job: 'Validation Job',
}

const STATUS_CONFIG = {
  pending: { label: 'Pending review', variant: 'warning' as const },
  approved: { label: 'Approved', variant: 'success' as const },
  changes_requested: { label: 'Changes requested', variant: 'danger' as const },
  rejected: { label: 'Rejected', variant: 'danger' as const },
  cancelled: { label: 'Cancelled', variant: 'default' as const },
}

// Demo data — in production, fetch from GET /api/v1/review-requests?status=pending
const DEMO_REQUESTS: ReviewRequest[] = [
  {
    request_id: 'rr_demo_001',
    resource_type: 'pre_read',
    resource_id: 'pr_demo_001',
    project_id: 'proj_demo',
    requested_by_user_id: 'user_junior_1',
    message: 'Ready for senior review. Insurance renewal messaging for Q1 cohort.',
    status: 'pending',
    created_at: Date.now() - 2 * 60 * 60 * 1000,
  },
  {
    request_id: 'rr_demo_002',
    resource_type: 'report',
    resource_id: 'rep_demo_001',
    project_id: 'proj_demo',
    requested_by_user_id: 'user_junior_2',
    message: 'Client-ready report draft. Please check the methodology appendix.',
    status: 'pending',
    created_at: Date.now() - 4 * 60 * 60 * 1000,
  },
  {
    request_id: 'rr_demo_003',
    resource_type: 'validation_job',
    resource_id: 'vj_demo_001',
    project_id: 'proj_demo_2',
    requested_by_user_id: 'user_junior_1',
    message: 'Simulated validation run complete. 47 respondents.',
    status: 'approved',
    created_at: Date.now() - 24 * 60 * 60 * 1000,
  },
]

export function ReviewQueuePage() {
  const [filter, setFilter] = useState<string>('pending')
  const [actioned, setActioned] = useState<Record<string, string>>({})
  const [commentInput, setCommentInput] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  const [requests, setRequests] = useState<ReviewRequest[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) {
      // Unauthenticated: fall back to demo requests for preview
      setRequests(DEMO_REQUESTS)
      setLoading(false)
      return
    }
    fetch('/api/v1/review-requests', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<ReviewRequest[]> : ([] as ReviewRequest[]))
      .then(rows => setRequests(rows.length > 0 ? rows : []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false))
  }, [])

  const displayed = requests.filter(r =>
    filter === 'all' ? true : (actioned[r.request_id] ?? r.status) === filter
  )

  function handleAction(requestId: string, action: 'approve' | 'reject' | 'request_changes') {
    const next = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'changes_requested'
    setActioned(prev => ({ ...prev, [requestId]: next }))
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl pb-20 md:pb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Review Queue</h1>
          <p className="text-sm text-slate-500 mt-1">Pre-reads, reports, and validation jobs awaiting senior review.</p>
        </div>
        <div className="flex gap-2">
          {(['pending', 'all']).map(f => (
            <button
              key={f}
              className={cn('text-sm px-3 py-1.5 rounded-lg border transition-colors', filter === f
                ? 'bg-echo-indigo text-white border-echo-indigo'
                : 'border-border-gray text-slate-500 hover:border-slate-300')}
              onClick={() => setFilter(f)}
            >
              {f === 'pending' ? 'Pending' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <Card className="text-center py-12">
          <p className="text-sm text-slate-400">Loading review queue…</p>
        </Card>
      )}

      {!loading && displayed.length === 0 && (
        <Card className="text-center py-12">
          <CheckCircle className="w-8 h-8 text-validation-green mx-auto mb-3" />
          <p className="text-sm font-medium text-deep-ink">No pending reviews</p>
          <p className="text-xs text-slate-500 mt-1">All caught up.</p>
        </Card>
      )}

      <div className="space-y-3">
        {displayed.map(req => {
          const Icon = RESOURCE_ICONS[req.resource_type]
          const effectiveStatus = actioned[req.request_id] ?? req.status
          const statusConfig = STATUS_CONFIG[effectiveStatus as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.pending
          const isPending = effectiveStatus === 'pending'
          const isExpanded = expanded === req.request_id
          const age = Math.round((Date.now() - req.created_at) / (60 * 60 * 1000))

          return (
            <Card key={req.request_id} className={cn(isPending && 'border-uncertainty-amber/40')}>
              <div className="flex items-start gap-3">
                <div className={cn('p-2 rounded-lg shrink-0', isPending ? 'bg-uncertainty-amber/10' : 'bg-soft-panel')}>
                  <Icon className={cn('w-4 h-4', isPending ? 'text-uncertainty-amber' : 'text-slate-400')} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-medium text-deep-ink">
                      {RESOURCE_LABELS[req.resource_type]}
                    </span>
                    <code className="text-xs text-slate-400">{req.resource_id}</code>
                    <Badge variant={statusConfig.variant} size="sm">{statusConfig.label}</Badge>
                  </div>
                  {req.message && <p className="text-xs text-slate-600 mb-2">{req.message}</p>}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {age}h ago</span>
                    <span>By {req.requested_by_user_id}</span>
                    <span>Project: {req.project_id}</span>
                  </div>

                  {isPending && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleAction(req.request_id, 'approve')}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Approve
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAction(req.request_id, 'request_changes')}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />Request changes
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-risk-rose hover:text-risk-rose"
                        onClick={() => handleAction(req.request_id, 'reject')}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                      </Button>
                      <button
                        className="text-xs text-slate-400 hover:text-deep-ink"
                        onClick={() => setExpanded(isExpanded ? null : req.request_id)}
                      >
                        {isExpanded ? 'Hide comment' : 'Add comment'}
                      </button>
                    </div>
                  )}

                  {isExpanded && isPending && (
                    <div className="mt-2 flex gap-2">
                      <input
                        className="flex-1 border border-border-gray rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-human-teal/30"
                        placeholder="Leave a comment…"
                        value={commentInput[req.request_id] ?? ''}
                        onChange={e => setCommentInput(prev => ({ ...prev, [req.request_id]: e.target.value }))}
                      />
                      <Button variant="ghost" size="sm">Send</Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
